import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import { catchError, map, of, switchMap } from 'rxjs';
import { ToolbarComponent } from '../../../shared/components/toolbar/toolbar.component';
import { ICommunity, ICommunityMember } from '../../model/community.model';
import { CommunityService } from '../../services/community.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'clp-community-subscribers',
    templateUrl: './communitySubscribers.component.html',
    styleUrls: ['./communitySubscribers.component.scss'],
    imports: [
        CommonModule,
        RouterModule,
        TranslateModule,
        TranslocoDatePipe,
        ToolbarComponent
    ],
    providers: [
        CommunityService
    ]
})
export class CommunitySubscribersComponent implements OnInit {
    private static readonly SUBSCRIPTION_TIMESTAMP_KEYS = [
        'createdat',
        'createdon',
        'createddate',
        'subscribedat',
        'subscriptiontimestamp',
        'subscriptiondate',
        'joinedat',
        'joindate'
    ];

    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private communityService = inject(CommunityService);

    community: ICommunity | null = null;
    loading = true;
    searchTerm = '';

    ngOnInit(): void {
        this.route.paramMap.pipe(
            map((params) => params.get('id')),
            switchMap((id) => {
                this.loading = true;

                if (!id) {
                    return of(null);
                }

                return this.communityService.getCommunity(id).pipe(
                    map((community) => community ?? null),
                    catchError(() => of(null))
                );
            })
        ).subscribe((community) => {
            this.community = community;
            this.loading = false;
        });
    }

    backToCommunities(): void {
        this.router.navigate(['listCommunities']);
    }

    onSearchChange(event: Event): void {
        this.searchTerm = (event.target as HTMLInputElement).value;
    }

    getFilteredMembers(members: ICommunityMember[]): ICommunityMember[] {
        const search = this.searchTerm.trim().toLowerCase();
        const filtered = !search
            ? members
            : members.filter((member) => (member.name ?? '').toLowerCase().includes(search));

        return [...filtered].sort((a, b) =>
            this.getTime(this.getSubscriptionTimestamp(b)) - this.getTime(this.getSubscriptionTimestamp(a))
        );
    }

    getNamedSubscribersCount(members: ICommunityMember[]): number {
        return members.filter((member) => !!member.name?.trim()).length;
    }

    getMemberTrackBy(member: ICommunityMember, index: number): string {
        return member.id ?? member.userId ?? member.name ?? `member-${index}`;
    }

    getMemberInitial(member: ICommunityMember): string {
        const name = member.name?.trim();
        if (name) {
            return name.charAt(0).toUpperCase();
        }

        return '?';
    }

    getMemberPictureUrl(pictureUrl: string | null | undefined): string | null {
        const normalizedPictureUrl = pictureUrl?.trim();
        if (!normalizedPictureUrl) {
            return null;
        }

        if (
            normalizedPictureUrl.startsWith('http://')
            || normalizedPictureUrl.startsWith('https://')
            || normalizedPictureUrl.startsWith('data:')
            || normalizedPictureUrl.startsWith('blob:')
        ) {
            return normalizedPictureUrl;
        }

        const sanitizedPictureUrl = normalizedPictureUrl.replace(/\\/g, '/');

        if (sanitizedPictureUrl.startsWith('/posts/images/')) {
            return `${environment.API_URL}${sanitizedPictureUrl}`;
        }

        const fileName = sanitizedPictureUrl.split('/').filter(Boolean).pop();
        if (!fileName) {
            return null;
        }

        return `${environment.API_URL}/posts/images/${fileName}`;
    }

    getSubscriptionTimestamp(member: ICommunityMember): Date | null {
        const directDate = this.toDate(member.CreatedAt ?? member.createdAt ?? null);
        if (directDate) {
            return directDate;
        }

        const rawValue = this.findValueByKeysDeep(
            member,
            CommunitySubscribersComponent.SUBSCRIPTION_TIMESTAMP_KEYS,
            0
        );

        return this.toDate(rawValue);
    }

    private findValueByKeysDeep(value: unknown, keys: string[], depth: number): unknown {
        if (value === undefined || value === null || depth > 4) {
            return null;
        }

        if (Array.isArray(value)) {
            for (const item of value) {
                const found = this.findValueByKeysDeep(item, keys, depth + 1);
                if (found !== null && found !== undefined) {
                    return found;
                }
            }

            return null;
        }

        if (!this.isRecord(value)) {
            return null;
        }

        for (const [key, currentValue] of Object.entries(value)) {
            if (currentValue === undefined || currentValue === null) {
                continue;
            }

            if (keys.includes(this.normalizeKey(key))) {
                return currentValue;
            }
        }

        for (const nestedValue of Object.values(value)) {
            const found = this.findValueByKeysDeep(nestedValue, keys, depth + 1);
            if (found !== null && found !== undefined) {
                return found;
            }
        }

        return null;
    }

    private toDate(value: unknown): Date | null {
        if (value === undefined || value === null) {
            return null;
        }

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        if (typeof value === 'number') {
            return this.dateFromNumber(value);
        }

        if (typeof value === 'string') {
            const trimmedValue = value.trim();
            if (!trimmedValue) {
                return null;
            }

            const parsedDate = new Date(trimmedValue);
            if (!Number.isNaN(parsedDate.getTime())) {
                return parsedDate;
            }

            const numericValue = Number(trimmedValue);
            return Number.isFinite(numericValue) ? this.dateFromNumber(numericValue) : null;
        }

        if (this.isRecord(value)) {
            for (const [key, nestedValue] of Object.entries(value)) {
                const normalizedKey = this.normalizeKey(key);
                if (
                    normalizedKey === 'date'
                    || normalizedKey === 'datetime'
                    || normalizedKey === 'timestamp'
                    || normalizedKey === 'value'
                    || normalizedKey === 'ticks'
                    || normalizedKey === 'unix'
                    || normalizedKey === 'unixseconds'
                    || normalizedKey === 'unixmilliseconds'
                    || normalizedKey === 'epoch'
                    || normalizedKey === 'epochtime'
                    || normalizedKey === 'seconds'
                    || normalizedKey === 'milliseconds'
                ) {
                    const nestedDate = this.toDate(nestedValue);
                    if (nestedDate) {
                        return nestedDate;
                    }
                }
            }
        }

        if (Array.isArray(value)) {
            for (const item of value) {
                const nestedDate = this.toDate(item);
                if (nestedDate) {
                    return nestedDate;
                }
            }
        }

        return null;
    }

    private dateFromNumber(value: number): Date | null {
        if (!Number.isFinite(value)) {
            return null;
        }

        const absoluteValue = Math.abs(value);
        let milliseconds: number;

        if (absoluteValue >= 1e16) {
            // .NET ticks (100ns since 0001-01-01)
            milliseconds = (value - 621355968000000000) / 10000;
        } else if (absoluteValue >= 1e12) {
            // Unix epoch milliseconds
            milliseconds = value;
        } else if (absoluteValue >= 1e9) {
            // Unix epoch seconds
            milliseconds = value * 1000;
        } else {
            return null;
        }

        const parsedDate = new Date(milliseconds);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    private normalizeKey(key: string): string {
        return key.toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null && !Array.isArray(value);
    }

    private getTime(value: Date | null | undefined): number {
        return value ? value.getTime() : 0;
    }
}
